// @flow
import { transact } from '@tldraw/state'
import type { TLStoreWithStatus, TLRecord } from '@tldraw/tldraw'
import { useEffect, useState } from 'react'
import { useOnePerDocumentItems } from '../CanvasConnectionProvider.jsx'
import type { ExportData } from '../CreativeCanvas'

/**
 * Sets up collaboration for the tldraw canvas. Returns the props that need to be passed to the tldraw canvas in order for a user to collaborate with other users.
 * Ensures that the tldraw canvas is in sync with the yjs document and vice versa.
 * @param {boolean} readOnly whether or not the user is allowed to edit the canvas. If true, the user will not be able to edit the canvas.
 * @param {string} documentId the document id used to connect to the correct yjs document.
 * @returns {Object} the props that need to be passed to the tldraw canvas in order for a user to collaborate with other users.
 */
export function useYjsStore(
	readOnly: boolean,
	documentId: string,
	exporting: ?ExportData
): TLStoreWithStatus {
	const {
		webSocketProvider,
		documentData: { shapes: yRecords, doc: yDoc },
		store,
		error,
	} = useOnePerDocumentItems(documentId, exporting?.status)

	const [storeWithStatus, setStoreWithStatus] = useState<TLStoreWithStatus>({
		status: 'loading',
	})

	// subscribes the yjs document to the tldraw store and vice versa.
	// Ensures that the tldraw store is available in the `storeWithStatus` state.
	useEffect(() => {
		setStoreWithStatus({
			status: 'loading',
		})

		const unsubs = []

		// flag that is set to true after the first time the subscriptions are setup.
		let hasSetupSubscriptionsBefore = false

		/**
		 * Function called when the websocket 'sync' event is emitted. Sets up the subscriptions between the yjs document and the tldraw store.
		 * Uses `hasSetupSubscriptionsBefore` to prevent creating the subscriptions multiple times. After the sync is complete,
		 * calls `setStoreWithStatus` to update the store status to 'synced-remote' and 'online'.
		 */
		function handleSync(isSynced: boolean) {
			// This condition makes sure we only setup the subscriptions once per call to this `useEffect`. This is ok because once
			// we're synced once, the yjs websocket along with the subscriptions we have setup will keep everything in sync.
			if (!isSynced || hasSetupSubscriptionsBefore) return

			hasSetupSubscriptionsBefore = true

			// Replace the store records with the yjs doc records
			transact(() => {
				store.clear()
				store.put([...yRecords.values()], 'initialize')
				store.ensureStoreIsUsable()
			})

			// Sync the yjs doc changes to the store
			const handleChange = (events, transaction) => {
				if (transaction.local) return
				const toRemove = []
				const toPut: TLRecord[] = []
				events.forEach(event => {
					event.changes.keys.forEach((change, id) => {
						switch (change.action) {
							case 'add':
							case 'update': {
								const record = yRecords.get(id)
								if (record) {
									toPut.push(record)
								}
								break
							}
							case 'delete': {
								toRemove.push(id)
								break
							}
							default:
								return
						}
					})
				})
				// put / remove the records in the store
				store.mergeRemoteChanges(() => {
					if (toRemove.length) store.remove(toRemove)
					if (toPut.length) store.put(toPut)
				})
			}
			yRecords.observeDeep(handleChange)
			unsubs.push(() => yRecords.unobserveDeep(handleChange))

			setStoreWithStatus({
				store,
				status: 'synced-remote',
				connectionStatus: 'online',
			})
		}

		/**
		 * Function called when the websocket status changes. Updates the `storeWithStatus` state to the appropriate status. In most
		 * cases, we hope that this function doesn't do anything, because `handleSync` sets the initial statuses and then the websocket
		 * status will never change. However if the websocket is disconnected, it will go through this function and the state will be updated to reflect
		 * that. In the case where the websocket is disconnected/connecting, we don't need to revert to a loading state because the yjs doc and tldraw store
		 * are still in sync, so once the websocket reconnects the updates will be forwarded to the server. In other words, we hook up the yjs doc with the
		 * tldraw store, and then let the yjs websocket manage the rest.
		 */
		function handleStatusChange({
			status,
		}: {
			status: 'disconnected' | 'connected' | 'connecting',
		}) {
			// If the websocket is disconnected, set the store status to 'synced-remote' and the connection status to 'offline'
			if (status === 'disconnected') {
				setStoreWithStatus({
					store,
					status: 'synced-remote',
					connectionStatus: 'offline',
				})
				return
			}

			if (status === 'connected') {
				// only set the store status to online if the subscriptions have already been setup. If they have
				// not been setup, the store status will be set in `handleSync` after subscriptions are setup.
				if (hasSetupSubscriptionsBefore) {
					setStoreWithStatus({
						store,
						status: 'synced-remote',
						connectionStatus: 'online',
					})
				}
			}
		}

		webSocketProvider.on('status', handleStatusChange)
		unsubs.push(() => webSocketProvider.off('status', handleStatusChange))
		webSocketProvider.on('sync', handleSync)
		unsubs.push(() => webSocketProvider.off('sync', handleSync))

		if (webSocketProvider.synced && webSocketProvider.wsconnected) {
			// handleSync needs to be called at least once when this useEffect runs so that the subscriptions between
			// the yjs doc and the tldraw store are setup. If the websocket is already connected and synced, then we need to call
			// it manually.
			handleSync(true)
		}

		return () => {
			unsubs.forEach(fn => fn())
			unsubs.length = 0
		}
	}, [store, webSocketProvider, yRecords, yDoc])

	return error ? { status: 'error', error } : storeWithStatus
}
